import { useState } from 'react';
import type { Attachment, GlobalTag, Item, List, ListTag, Priority } from '../db/types';
import { colorOf } from '../lib/colors';
import { formatDueShort, formatTime, hasTime } from '../lib/dates';
import { AttachmentGallery } from './AttachmentGallery';
import { DatePicker } from './DatePicker';
import { TagField, type TagOption } from './TagField';
import './SidePanel.css';

interface Props {
  item: Item;
  lists: List[];
  /** Every list tag; the panel narrows to the item's own list. */
  listTags: ListTag[];
  globalTags: GlobalTag[];
  attachments: Attachment[];
  onPatch: (patch: Partial<Item>) => void;
  /** Moving list is not an ordinary patch — it drops the list tags. */
  onMoveList: (listId: string) => void;
  onNewList: () => void;
  onCreateListTag: (name: string) => void;
  onEditListTag: (tag: TagOption) => void;
  onCreateGlobalTag: (name: string) => void;
  onEditGlobalTag: (tag: TagOption) => void;
  onOpenImage: (index: number) => void;
  onClose: () => void;
}

type Tab = 'properties' | 'attachments';
type DateField = 'due_at' | 'start_at' | 'remind_at';

const PRIORITIES: Priority[] = [1, 2, 3, 4];

const DATE_FIELDS: Array<{ field: DateField; label: string }> = [
  { field: 'due_at', label: 'Due date' },
  { field: 'start_at', label: 'Start date' },
  { field: 'remind_at', label: 'Remind at' },
];

const asOption = (t: ListTag | GlobalTag): TagOption => ({
  id: t.id,
  name: t.name,
  color: t.color,
});

/** List, list tags, global tags, priority, dates. */
export function PropertiesPanel({
  item,
  lists,
  listTags,
  globalTags,
  attachments,
  onPatch,
  onMoveList,
  onNewList,
  onCreateListTag,
  onEditListTag,
  onCreateGlobalTag,
  onEditGlobalTag,
  onOpenImage,
  onClose,
}: Props) {
  const [tab, setTab] = useState<Tab>('properties');
  const [open, setOpen] = useState<DateField | null>(null);

  const list = lists.find((l) => l.id === item.list_id);
  const listColor = colorOf(list?.color);
  const ownTags = listTags.filter((t) => t.list_id === item.list_id);

  return (
    <aside className="side side--right" aria-label="Properties">
      <div className="side__tabs">
        <button
          type="button"
          className={`side__tab${tab === 'properties' ? ' is-active' : ''}`}
          onClick={() => setTab('properties')}
        >
          Properties
        </button>
        <button
          type="button"
          className={`side__tab${tab === 'attachments' ? ' is-active' : ''}`}
          onClick={() => setTab('attachments')}
        >
          Attachments
          {attachments.length > 0 && <span className="side__badge">{attachments.length}</span>}
        </button>
        <span className="spacer" />
        <button type="button" className="side__close" onClick={onClose} aria-label="Close properties">
          ✕
        </button>
      </div>

      {tab === 'attachments' ? (
        <div className="side__body side__body--flush">
          <AttachmentGallery itemId={item.id} attachments={attachments} onOpen={onOpenImage} />
        </div>
      ) : (
        <div className="side__body">
          {/* ── List ─────────────────────────────────────── */}
          <div className="field">
            <span className="field__label">List</span>
            <div className="field__withDot">
              {listColor && (
                <span className="field__dot" style={{ background: listColor }} aria-hidden="true" />
              )}
              <select
                className="field__input field__select"
                value={item.list_id}
                aria-label="List"
                style={listColor ? { paddingLeft: '22px' } : undefined}
                onChange={(e) => {
                  if (e.target.value === '__new__') onNewList();
                  else onMoveList(e.target.value);
                }}
              >
                {lists.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.name}
                  </option>
                ))}
                <option value="__new__">New list…</option>
              </select>
            </div>
            <span className="field__hint">
              Every note lives in exactly one list. Moving it clears its list tags.
            </span>
          </div>

          {/* ── List tags ────────────────────────────────── */}
          <div className="field">
            <span className="field__label">List tags</span>
            <TagField
              value={item.list_tag_ids}
              options={ownTags.map(asOption)}
              placeholder="Add tag in this list…"
              onChange={(list_tag_ids) => onPatch({ list_tag_ids })}
              onCreate={onCreateListTag}
              onEdit={onEditListTag}
            />
            <span className="field__hint">
              What this note is about within {list?.name ?? 'this list'}.
            </span>
          </div>

          {/* ── Global tags ──────────────────────────────── */}
          <div className="field">
            <span className="field__label">Global tags</span>
            <TagField
              value={item.global_tag_ids}
              options={globalTags.map(asOption)}
              placeholder="Add global tag…"
              onChange={(global_tag_ids) => onPatch({ global_tag_ids })}
              onCreate={onCreateGlobalTag}
              onEdit={onEditGlobalTag}
            />
            <span className="field__hint">Cuts across every list. Survives a move.</span>
          </div>

          {/* ── Priority ─────────────────────────────────── */}
          <div className="field">
            <span className="field__label">Priority</span>
            <div className="prio">
              {PRIORITIES.map((p) => (
                <button
                  key={p}
                  type="button"
                  className={`prio__dot prio__dot--${p}${item.priority === p ? ' is-active' : ''}`}
                  title={p < 4 ? `P${p}` : 'None'}
                  aria-label={p < 4 ? `Priority ${p}` : 'No priority'}
                  aria-pressed={item.priority === p}
                  onClick={() => onPatch({ priority: p })}
                />
              ))}
            </div>
          </div>

          {/* ── Dates ────────────────────────────────────── */}
          <div className="field">
            <span className="field__label">Dates</span>
            {DATE_FIELDS.map(({ field, label }) => {
              const value = item[field];
              return (
                <div className="field__date" key={field}>
                  <button
                    type="button"
                    className={`field__input field__button${value ? '' : ' is-unset'}`}
                    onClick={() => setOpen(open === field ? null : field)}
                  >
                    <span>
                      {value
                        ? field === 'remind_at' && hasTime(value)
                          ? formatTime(value)
                          : formatDueShort(value)
                        : label}
                    </span>
                    <span className="field__cal" aria-hidden="true">
                      ▤
                    </span>
                  </button>
                  {open === field && (
                    <DatePicker
                      value={value}
                      onPick={(iso) => {
                        onPatch({ [field]: iso } as Partial<Item>);
                        setOpen(null);
                      }}
                      onClose={() => setOpen(null)}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </aside>
  );
}
